import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { publicApiClient } from '../api/apiClient';
import { UserAvatar } from './ui/user-avatar';
import { getRoleLabel } from '../utils/roleMapper';
import { Search2LineIconV7 } from './ui/figma-icons-v7';
import { UserEditForm } from './UserEditForm';
import { UpdateAccountRequest } from '../../generated-src/client/account/api';
import { SelectWithLabel, CompanySelectWithLabel } from './ui/universal-select';
import { useAccountsPagination } from '../hooks/useManagementPagination';
import { ManagementPagination } from './ui/ManagementPagination';
import { useAuthStore } from '../store/authStore';
import { isLimitedToOwnCompany } from '../utils/roleUtils';
import { useMyCompany } from '../hooks/useMyCompany';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../hooks/useBrandingColors';
import { loadCompaniesForSelect } from '../services/companySelectOptionsService';

// Локальные типы для работы с пользователями
enum RoleEnum {
  SUPER_ADMIN = 'SUPER_ADMIN',
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  RECRUITER = 'RECRUITER'
}

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: RoleEnum;
  teamId?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserSummary {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: RoleEnum;
}

interface UserCreateRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: RoleEnum;
  password: string;
  companyId?: number;
}


interface AddUserToTeamRequest {
  userId: number;
}

interface Company {
  id: number;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'PILOT' | 'SUSPENDED' | 'INACTIVE';
}

// Иконки для интерфейса в стиле проекта

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 4V16M4 10H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);


const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V16C2 16.5304 2.21071 17.0391 2.58579 17.4142C2.96086 17.7893 3.46957 18 4 18H14C14.5304 18 15.0391 17.7893 15.4142 17.4142C15.7893 17.0391 16 16.5304 16 16V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 6H5H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);




// Интерфейсы для типизации
interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  role: RoleEnum;
  password: string;
  confirmPassword: string;
  companyId?: number;
}


type ViewMode = 'list' | 'create' | 'edit' | 'user-edit';

interface AccountManagementContentProps {
  invitationMode?: boolean;
  teamId?: number | null;
  onTeamMembersUpdate?: () => void; // Callback для обновления списка команды
}

/**
 * Компонент управления аккаунтами пользователей
 * Приведен в соответствие с дизайном проекта
 */
export function AccountManagementContent({ invitationMode = false, teamId, onTeamMembersUpdate }: AccountManagementContentProps) {
  console.log('=== AccountManagementContent rendered ===', { invitationMode, teamId });
  
  const colors = useBrandingColors();
  
  const getCurrentRole = () => {
    const authStore = useAuthStore.getState();
    return authStore.user?.role || authStore.role;
  };
  
  const isCurrentUserSuperAdmin = () => {
    const role = getCurrentRole();
    return String(role || '').toUpperCase() === 'SUPER_ADMIN';
  };
  
  const isCurrentUserRecruiter = () => {
    const role = getCurrentRole();
    return String(role || '').toUpperCase() === 'RECRUITER';
  };
  
  const canCreateAccounts = () => {
    return !isCurrentUserRecruiter();
  };
  
  const canEditUser = (targetUserId: number) => {
    if (isCurrentUserRecruiter()) {
      const currentUserId = useAuthStore.getState().user?.id;
      return currentUserId === targetUserId;
    }
    return true;
  };
  
  const canDeleteUser = () => {
    if (isCurrentUserRecruiter()) return false;
    const currentRole = String(getCurrentRole() || '').toUpperCase();
    if (currentRole === 'SUPER_ADMIN') return true;

    return (userRole: string) => {
      const targetRole = String(userRole || '').toUpperCase();
      if (currentRole === 'OWNER') {
        return targetRole === 'ADMIN' || targetRole === 'RECRUITER';
      }
      if (currentRole === 'ADMIN') {
        return targetRole === 'RECRUITER';
      }
      return false;
    };
  };
  // Состояние для списка пользователей
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Пагинация
  const pagination = useAccountsPagination();
  
  // Информация о компании пользователя
  const { company: myCompany } = useMyCompany();
  
  // Состояние для поиска и фильтрации
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleEnum | ''>('');
  const [activeFilter, setActiveFilter] = useState<'ACTIVE' | 'INACTIVE' | 'ALL'>('ACTIVE');
  const [companyFilter, setCompanyFilter] = useState<number | '' | 'null'>('');
  const [isSearching, setIsSearching] = useState(false);
  const usersSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // Состояние для режима просмотра
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  
  // Состояние для формы
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    role: RoleEnum.RECRUITER,
    password: '',
    confirmPassword: ''
  });

  // Состояние для модалки удаления
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const currentRole = String(getCurrentRole() || '').toUpperCase();
    if (currentRole === 'ADMIN' && formData.role === RoleEnum.OWNER) {
      setFormData(prev => ({ ...prev, role: RoleEnum.RECRUITER }));
    }
  }, [formData.role]);

  // Состояние для участников команды
  const [teamMembers, setTeamMembers] = useState<UserSummary[]>([]);
  const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);
  
  // Состояние для компаний
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);

  // Состояние для проверки наличия владельца в компании
  const [companyHasOwner, setCompanyHasOwner] = useState(false);
  const [checkingOwner, setCheckingOwner] = useState(false);

  // Проверка наличия владельца в компании
  const checkCompanyOwner = useCallback(async (companyId: number | undefined | null) => {
    if (companyId == null || !isCurrentUserSuperAdmin()) {
      setCompanyHasOwner(false);
      return;
    }
    
    setCheckingOwner(true);
    try {
      const response = await publicApiClient.account.listAccounts(
        undefined,
        RoleEnum.OWNER as any,
        'ALL',
        companyId,
        0,
        1
      );
      setCompanyHasOwner((response.data?.totalElements || 0) > 0);
    } catch (err) {
      console.error('Ошибка при проверке владельца компании:', err);
      setCompanyHasOwner(false);
    } finally {
      setCheckingOwner(false);
    }
  }, []);

  // Проверяем владельца при изменении компании в форме создания
  useEffect(() => {
    if (viewMode === 'create' && formData.companyId != null) {
      checkCompanyOwner(formData.companyId);
    } else {
      setCompanyHasOwner(false);
    }
  }, [viewMode, formData.companyId, checkCompanyOwner]);

  // Debounce для поиска
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Загрузка пользователей команды (выполняется один раз)
  const fetchTeamMembers = useCallback(async () => {
    if (teamMembersLoaded || !teamId) return;
    
    try {
      console.log('🔍 Загружаем пользователей команды...');
      const response = await publicApiClient.teams.getTeamUsersList(teamId);
      console.log('🔍 Получены пользователи команды:', response.data);
      setTeamMembers((response.data || []) as any[]);
      setTeamMembersLoaded(true);
    } catch (err) {
      console.error('❌ Ошибка при загрузке пользователей команды:', err);
      setTeamMembers([]);
      setTeamMembersLoaded(true);
    }
  }, [teamId, teamMembersLoaded]);

  // Загрузка компаний (выполняется один раз, только для SUPER_ADMIN)
  const fetchCompanies = useCallback(async () => {
    if (companiesLoaded) return;
    
    const currentRole = getCurrentRole();
    
    // Для админа и рекрутера не загружаем список компаний, так как они могут создавать пользователей только в своей компании
    if (isLimitedToOwnCompany(currentRole as any)) {
      console.log('🔍 Пропускаем загрузку компаний для', currentRole);
      setCompanies([]);
      setCompaniesLoaded(true);
      return;
    }
    
    try {
      console.log('🔍 Загружаем компании...');
      const loadedCompanies = await loadCompaniesForSelect();
      console.log('🔍 Получены компании:', loadedCompanies);
      setCompanies(loadedCompanies as Company[]);
      setCompaniesLoaded(true);
    } catch (err) {
      console.error('❌ Ошибка при загрузке компаний:', err);
      setCompanies([]);
      setCompaniesLoaded(true);
    }
  }, [companiesLoaded]);

  // Загрузка пользователей
  const fetchUsers = useCallback(async (page = 0, size = 20) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Загружаем пользователей...');
      console.log('🔍 Параметры запроса:', { search: debouncedSearchTerm, role: roleFilter, active: activeFilter, companyId: companyFilter, page: pagination.currentPage - 1, size: pagination.itemsPerPage });
      
      // Используем account API для получения списка аккаунтов
      // В режиме приглашения не используем фильтр компании, так как админы и рекрутеры работают в рамках одной компании
      const companyIdFilter = invitationMode 
        ? undefined 
        : (companyFilter === '' ? undefined : (companyFilter === 'null' ? undefined : Number(companyFilter)));
        
      const response = await publicApiClient.account.listAccounts(
        debouncedSearchTerm || undefined,
        (roleFilter || undefined) as any,
        activeFilter,
        companyIdFilter, 
        pagination.currentPage - 1, // API использует 0-based индексацию
        pagination.itemsPerPage
      );
      
      console.log('🔍 Получен ответ:', response.data);
      console.log('🔍 Pagination data:', {
        requestedPage: pagination.currentPage - 1,
        requestedSize: pagination.itemsPerPage,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        content: response.data.content?.length,
        actualContent: response.data.content
      });
      setUsers((response.data.content || []) as any[]);
      
      // Обновляем состояние пагинации
      pagination.setTotalItems(response.data.totalElements || 0);
    } catch (err) {
      console.error('❌ Ошибка при загрузке пользователей:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, roleFilter, activeFilter, companyFilter, invitationMode, pagination.currentPage, pagination.itemsPerPage, pagination.setTotalItems]);

  // Создание пользователя
  const createUser = async (userData: UserFormData) => {
    try {
      setError(null); // Очищаем предыдущие ошибки
      
      if (invitationMode) {
        // В режиме приглашения отправляем приглашение
        await inviteUserToTeam(userData);
      } else {
        // В режиме управления создаем пользователя
        const userCreateRequest: UserCreateRequest = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          role: userData.role,
          password: userData.password || '',
          // ВАЖНО: прокидываем companyId при создании, иначе компания не установится
          companyId: userData.companyId
        };
        
        // Используем account API для создания аккаунта
        const response = await publicApiClient.account.createAccount({
          email: userCreateRequest.email,
          firstName: userCreateRequest.firstName,
          lastName: userCreateRequest.lastName,
          role: userCreateRequest.role as any,
          password: userCreateRequest.password,
          companyId: userCreateRequest.companyId
        });
        await fetchUsers();
      }
      
      setViewMode('list');
      await resetForm();
    } catch (err: any) {
      console.error('Ошибка при создании пользователя:', err);
      
      // Извлекаем детальную информацию об ошибке
      let errorMessage = 'Ошибка при создании пользователя';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  // Приглашение пользователя в команду
  const inviteUserToTeam = async (userData: UserFormData) => {
    try {
      setError(null); // Очищаем предыдущие ошибки
      
      // Сначала создаем пользователя через account API
      const newUser = await publicApiClient.account.createAccount({
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role as any,
        password: userData.password,
        companyId: userData.companyId
      });

      // Затем приглашаем его в команду через teams API
      if (teamId) {
        await publicApiClient.teams.addUserToTeam(teamId, {
          userId: newUser.data.id
        });
      }
      
      console.log('Пользователь создан и приглашен в команду:', newUser.data);
      setViewMode('list');
      await resetForm();
      await fetchUsers();
    } catch (err: any) {
      console.error('Ошибка при создании и приглашении пользователя:', err);
      
      // Извлекаем детальную информацию об ошибке
      let errorMessage = 'Ошибка при создании и приглашении пользователя';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  // Вспомогательная функция для приглашения пользователя в команду по ID
  const inviteUserToTeamById = async (userId: number) => {
    try {
      console.log('=== inviteUserToTeamById called ===', { userId, teamId });
      
      // Используем переданный teamId или ищем команду пользователя
      let targetTeamId: number;
      
      if (teamId) {
        // Используем переданный teamId
        targetTeamId = teamId;
        console.log('Using passed teamId:', targetTeamId);
      } else {
        // Fallback: ищем команду пользователя
        console.log('No teamId passed, searching for user teams...');
        const teamsResponse = await publicApiClient.teams.listTeams();
        console.log('Teams response:', teamsResponse);
        const teamsData = teamsResponse.data as any;
        
        if (teamsData.content && teamsData.content.length > 0) {
          targetTeamId = teamsData.content[0].id;
          console.log('Found teamId from search:', targetTeamId);
        } else {
          throw new Error('Нет доступных команд для приглашения пользователя');
        }
      }
      
      const addUserRequest: AddUserToTeamRequest = { userId };
      console.log('Adding user to team:', { teamId: targetTeamId, userId, request: addUserRequest });
      
      console.log('Calling apiClient.teams.addUserToTeam...');
              const response = await publicApiClient.teams.addUserToTeam(targetTeamId, addUserRequest);
      console.log('=== DETAILED API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('StatusText:', response.statusText);
      console.log('Data:', response.data);
      console.log('Headers:', response.headers);
      console.log('Config:', response.config);
      console.log('=== END API RESPONSE ===');
      
      // Проверяем что именно вернул бэкенд
      if (response.data) {
        console.log('Response data type:', typeof response.data);
        console.log('Response data keys:', Object.keys(response.data));
        if (response.data.id) {
          console.log('User added with ID:', response.data.id);
        }
      }
      
      setError(null);
      return true;
    } catch (err) {
      console.error('Ошибка при добавлении пользователя в команду:', err);
      setError('Ошибка при приглашении пользователя в команду');
      return false;
    }
  };

  // Функция для приглашения пользователя в команду
  const inviteUser = async (user: User) => {
    // Проверяем, не находится ли пользователь уже в команде
    if (isUserInTeam(user)) {
      console.log('Пользователь уже в команде:', user.email);
      return;
    }

    try {
      console.log('=== inviteUser called ===', { user, teamId, invitationMode });
      setLoading(true);
      setError(null);
      
      console.log('Calling inviteUserToTeamById with userId:', user.id);
      const ok = await inviteUserToTeamById(user.id);
      console.log('inviteUserToTeamById result:', ok);
      
      if (ok) {
        console.log('=== SUCCESS: User invited successfully ===');
        console.log('User ID:', user.id);
        console.log('User name:', user.firstName, user.lastName);
        console.log('User email:', user.email);
        
        // Добавляем пользователя в список команды
        setTeamMembers(prev => [...prev, user]);
        
        // Убираем пользователя из списка доступных для приглашения
        setUsers(prev => prev.filter(u => u.id !== user.id));
        
        setError(null);
        
        // Уведомляем родительский компонент об обновлении списка команды
        if (onTeamMembersUpdate) {
          console.log('Calling onTeamMembersUpdate callback');
          onTeamMembersUpdate();
        }
      } else {
        console.log('=== FAILURE: User invitation failed ===');
        console.log('User ID:', user.id);
        console.log('User name:', user.firstName, user.lastName);
        setError('Не удалось пригласить пользователя в команду');
      }
    } catch (err) {
      console.error('Ошибка при приглашении пользователя:', err);
      setError('Ошибка при приглашении пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Обновление пользователя
  const updateUser = async (id: number, userData: Partial<UserFormData>) => {
    try {
      console.log('🔍 updateUser: отправляем данные:', userData);
      
      // Создаем объект только с измененными полями с правильной типизацией
      const userUpdateRequest: UpdateAccountRequest = {
        firstName: userData.firstName || '',
        lastName: userData.lastName || ''
      };
      
      if (userData.email !== undefined) userUpdateRequest.email = userData.email;
      if (userData.role !== undefined) userUpdateRequest.role = userData.role;
      
      console.log('🔍 updateUser: userUpdateRequest:', userUpdateRequest);
      
      // Используем account API для обновления аккаунта
      await publicApiClient.account.updateAccountById(id, userUpdateRequest);
      await fetchUsers();
      
      setViewMode('list');
      await resetForm();
    } catch (err) {
      console.error('Ошибка при обновлении пользователя:', err);
      setError('Ошибка при обновлении пользователя');
    }
  };

  // Открытие модалки удаления
  const openDeleteModal = (user: User) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
    setDeleteError(null); // Очищаем предыдущие ошибки
  };

  // Закрытие модалки удаления
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
    setDeleteError(null); // Очищаем ошибки при закрытии
  };

  // Удаление пользователя
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeleteError(null); // Очищаем предыдущие ошибки
      await publicApiClient.account.deleteAccountById(userToDelete.id);
      closeDeleteModal();
      await fetchUsers();
    } catch (err: any) {
      console.error('Ошибка при удалении пользователя:', err);
      
      // Извлекаем детальную информацию об ошибке
      let errorMessage = 'Не удалось удалить пользователя';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setDeleteError(errorMessage);
      // Не закрываем модальное окно, чтобы пользователь видел ошибку
    }
  };

  const confirmDeactivateUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteError(null);
      const update: UpdateAccountRequest = {
        firstName: userToDelete.firstName,
        lastName: userToDelete.lastName,
        email: userToDelete.email,
        role: userToDelete.role as any,
        isActive: false
      };
      await publicApiClient.account.updateAccountById(userToDelete.id, update);
      closeDeleteModal();
      await fetchUsers();
    } catch (err: any) {
      console.error('Ошибка при деактивации пользователя:', err);
      let errorMessage = 'Не удалось перевести пользователя в неактивные';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    }
  };

  // Сброс формы
  const resetForm = async () => {
    // Получаем информацию о текущем пользователе для автоматической установки компании
    const currentUser = useAuthStore.getState().user;
    const currentUserRole = getCurrentRole();
    const role = String(currentUserRole).toUpperCase();
    const adminCompanyId = (currentUser as any)?.companyId;
    
    console.log('🔍 resetForm - currentUser:', currentUser);
    console.log('🔍 resetForm - currentUserRole:', currentUserRole);
    console.log('🔍 resetForm - role:', role);
    console.log('🔍 resetForm - adminCompanyId:', adminCompanyId);
    
    let finalCompanyId = undefined;
    
    if (isLimitedToOwnCompany(role as any)) {
      // Если companyId есть в пользователе, используем его
      if (adminCompanyId) {
        finalCompanyId = adminCompanyId;
        console.log('🔍 resetForm - Using companyId from user profile:', adminCompanyId);
      } else {
        console.log('🔍 resetForm - No companyId in user profile, but this should not happen for', role, 'role');
        // Для админа и рекрутера companyId должен быть в профиле, если его нет - это ошибка конфигурации
      }
    }
    
    console.log('🔍 resetForm - finalCompanyId:', finalCompanyId);
    
    const newFormData = {
      firstName: '',
      lastName: '',
      email: '',
      role: RoleEnum.RECRUITER,
      password: '',
      confirmPassword: '',
      companyId: finalCompanyId // Автоматически устанавливаем компанию админа
    };
    
    console.log('🔍 resetForm - setting formData:', newFormData);
    setFormData(newFormData);
    
    console.log('🔍 resetForm - formData set with companyId:', finalCompanyId);
  };

  // Открытие режима редактирования пользователя через UserEditForm
  const openUserEditMode = (user: User) => {
    setEditingUserId(user.id);
    setViewMode('user-edit');
  };


  // Открытие режима создания (только в режиме управления)
  const openCreateMode = async () => {
    if (!invitationMode) {
      await resetForm();
      setSelectedUser(null);
      setError(null); // Очищаем ошибки при переходе в режим создания
      setViewMode('create');
    }
  };

  // Возврат к списку
  const backToList = async () => {
    setViewMode('list');
    setSelectedUser(null);
    setEditingUserId(null);
    setError(null); // Очищаем ошибки при возврате к списку
    await resetForm();
  };

  // Обработка успешного сохранения пользователя
  const handleUserEditSuccess = async () => {
    // Обновляем список пользователей
    fetchUsers();
    // Возвращаемся к списку
    await backToList();
  };

  // Функция для валидации пароля
  const validatePassword = (password: string, confirmPassword: string) => {
    const errors: { password?: string; confirmPassword?: string } = {};
    
    // Валидируем пароль только если он заполнен
    if (password && password.length < 8) {
      errors.password = 'Пароль должен содержать минимум 8 символов';
    }
    
    // Валидируем подтверждение пароля только если пароль заполнен
    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }
    
    return errors;
  };

  // Обработка отправки формы
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Валидация пароля для режима создания
    if (viewMode === 'create') {
      const passwordErrors = validatePassword(formData.password, formData.confirmPassword);
      if (Object.keys(passwordErrors).length > 0) {
        // Показываем первую ошибку
        const firstError = Object.values(passwordErrors)[0];
        setError(firstError);
        return;
      }
    }
    
    if (viewMode === 'edit' && selectedUser) {
      console.log('🔍 handleSubmit: редактирование аккаунта');
      console.log('🔍 handleSubmit: текущие данные формы:', formData);
      console.log('🔍 handleSubmit: исходные данные пользователя:', selectedUser);
      
      // Для редактирования передаем все актуальные данные формы
      const updateData: Partial<UserFormData> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role
      };
      
      console.log('🔍 handleSubmit: данные для обновления:', updateData);
      updateUser(selectedUser.id, updateData);
    } else {
      console.log('🔍 handleSubmit: создание нового аккаунта');
      createUser(formData);
    }
  };

  // Фильтрация теперь происходит на сервере, используем данные напрямую
  const filteredUsers = users;

  // Для админа и рекрутера добавляем их компанию в список компаний
  const companiesForSelect = useMemo(() => {
    const currentUser = useAuthStore.getState().user;
    const currentRole = String((currentUser?.role || useAuthStore.getState().role || '').toString().toUpperCase());
    if (currentRole === 'SUPER_ADMIN') {
      return companies;
    }
    if (myCompany) {
      return [{ id: myCompany.id, name: myCompany.name }];
    }
    return [];
  }, [companies, myCompany]);

  // Фиксируем фильтр компании на компании текущего администратора только для не-SUPER_ADMIN
  useEffect(() => {
    const currentUser = useAuthStore.getState().user;
    const currentRole = String((currentUser?.role || useAuthStore.getState().role || '').toString().toUpperCase());
    if (currentRole !== 'SUPER_ADMIN' && !invitationMode && myCompany && companyFilter !== myCompany.id) {
      setCompanyFilter(myCompany.id);
    }
  }, [invitationMode, myCompany, companyFilter]);

  // Сбрасываем фильтр ролей для ADMIN, если он выбрал SUPER_ADMIN
  useEffect(() => {
    const currentUser = useAuthStore.getState().user;
    const currentRole = String((currentUser?.role || useAuthStore.getState().role || '').toString().toUpperCase());
    if (currentRole === 'ADMIN' && roleFilter === RoleEnum.SUPER_ADMIN) {
      setRoleFilter('');
    }
  }, [roleFilter]);

  // Загрузка при монтировании
  useEffect(() => {
    console.log('🔍 AccountManagementContent: useEffect сработал');
    fetchUsers();
    fetchCompanies();
    
    // В режиме приглашения загружаем пользователей команды
    if (invitationMode && teamId) {
      fetchTeamMembers();
    }
  }, [invitationMode, teamId, fetchTeamMembers, fetchCompanies, fetchUsers]);

  // Перезагрузка данных при изменении фильтров
  useEffect(() => {
    console.log('🔍 AccountManagementContent: фильтры изменились, перезагружаем данные');
    pagination.setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  }, [debouncedSearchTerm, roleFilter, activeFilter, invitationMode, companyFilter, pagination.setCurrentPage]);

  // Перезагрузка данных при изменении страницы
  useEffect(() => {
    if (pagination.currentPage > 0) {
      console.log('🔍 AccountManagementContent: страница изменилась, перезагружаем данные');
      fetchUsers();
    }
  }, [pagination.currentPage, fetchUsers]);



  // Проверка, находится ли пользователь в команде
  const isUserInTeam = (user: User): boolean => {
    return teamMembers.some(teamMember => teamMember.id === user.id);
  };

  // Получение заголовка в зависимости от режима
  const getHeaderTitle = () => {
    if (invitationMode) {
      return 'Приглашение в команду';
    }
    
    switch (viewMode) {
      case 'create': return 'Создание пользователя';
      case 'edit': return 'Редактирование пользователя';
      case 'user-edit': return 'Редактирование пользователя';
      default: return 'Управление аккаунтами';
    }
  };

  // Получение текста кнопки действия
  const getActionButtonText = () => {
    if (invitationMode) {
      return 'Отправить приглашение';
    }
    
    switch (viewMode) {
      case 'create': return 'Создать';
      case 'edit': return 'Сохранить';
      case 'user-edit': return 'Сохранить';
      default: return 'Создать пользователя';
    }
  };

  // Рендер списка пользователей
  const renderUsersList = () => {
    console.log('=== renderUsersList called ===', { invitationMode, teamId, usersCount: filteredUsers.length });
    
    return (
    <>
      {/* Header with Create Button */}
      <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
          <p className="block leading-[30px] whitespace-pre">{getHeaderTitle()}</p>
        </div>
        
        {/* Кнопка создания только в режиме управления и не для рекрутеров */}
        {!invitationMode && canCreateAccounts() && (
          <button
            onClick={openCreateMode}
            className="box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-[10px] relative rounded-2xl shrink-0 transition-colors"
            style={getButtonInlineStyles(colors, 'primary')}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary'));
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary'));
            }}
            title="Создать пользователя"
          >
            <PlusIcon />
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                          <div className="css-rpndqk font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px] text-white">
              <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                Создать пользователя
              </p>
            </div>
            </div>
          </button>
        )}
      </div>

      {/* Filters and Search */}
      <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start relative shrink-0 w-full">

        <div className="box-border content-stretch flex flex-row gap-4 items-end justify-start p-0 relative shrink-0 w-full">
          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 flex-1">
            <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
              <p className="block leading-[20px]">{invitationMode ? 'Поиск пользователей для приглашения' : 'Поиск пользователей'}</p>
            </label>
            <div className="relative w-full group cursor-text" onClick={() => usersSearchInputRef.current?.focus()}>
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#868c98] z-10">
                <Search2LineIconV7 className="w-5 h-5" />
              </div>
              <div className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-2xl group-focus-within:border-[var(--brand-primary,#e16349)]" />
              <input
                type="text"
                placeholder="Поиск по имени, фамилии или email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] pr-[24px] pl-[48px] border-none focus:outline-none h-[44px] text-[16px] placeholder:text-[#868c98]"
                ref={usersSearchInputRef}
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10" style={{ color: colors.primary }}>
                  <div className="animate-spin w-4 h-4 border-2 border-t-transparent rounded-full" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }}></div>
                </div>
              )}
            </div>
          </div>
          
          <div className="w-[200px]">
            <SelectWithLabel
              label="Роль"
              value={roleFilter}
              onChange={(value) => setRoleFilter(value as RoleEnum | '')}
              options={(() => {
                const currentRole = String(getCurrentRole() || '').toUpperCase();
                const baseOptions = [
                  { value: '', label: 'Все роли' },
                  { value: RoleEnum.OWNER, label: 'Владелец' },
                  { value: RoleEnum.ADMIN, label: 'Администратор' },
                  { value: RoleEnum.RECRUITER, label: 'Рекрутер' }
                ];
                
                // Только SUPER_ADMIN может видеть и фильтровать по роли SUPER_ADMIN
                if (currentRole === 'SUPER_ADMIN') {
                  baseOptions.splice(1, 0, { value: RoleEnum.SUPER_ADMIN, label: 'Суперадминистратор' });
                }
                
                return baseOptions;
              })()}
            />
          </div>

          {!invitationMode && (
            <div className="w-[200px]">
              <SelectWithLabel
                label="Статус"
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as 'ACTIVE' | 'INACTIVE' | 'ALL')}
                options={[
                  { value: 'ACTIVE', label: 'Активные' },
                  { value: 'INACTIVE', label: 'Неактивные' },
                  { value: 'ALL', label: 'Все' }
                ]}
              />
            </div>
          )}
          
          {/* Скрываем фильтр Компания в режиме приглашения в команду */}
          {!invitationMode && (
            <div className="w-[250px]">
              {isCurrentUserSuperAdmin() ? (
                <CompanySelectWithLabel
                  label="Компания"
                  value={companyFilter}
                  onChange={(value) => setCompanyFilter(value === 'null' ? 'null' : (value ? Number(value) : ''))}
                  companies={companiesForSelect}
                  includeAll={true}
                  includeNone={true}
                />
              ) : (
                <CompanySelectWithLabel
                  label="Компания"
                  value={myCompany?.id || 'null'}
                  onChange={() => { /* фильтр компании отключен для администратора */ }}
                  companies={companiesForSelect}
                  includeAll={false}
                  includeNone={false}
                  disabled={true}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users List */}
      <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-0 relative shrink-0 w-full">
        {loading ? (
          <div className="bg-[#ffffff] box-border content-stretch flex flex-col gap-4 items-center justify-center p-[32px] relative rounded-[32px] shrink-0 w-full">
            <div className="css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center w-full">
              <p className="block leading-[24px]">Загрузка пользователей...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-[#ffffff] box-border content-stretch flex flex-col gap-4 items-center justify-center p-[32px] relative rounded-[32px] shrink-0 w-full">
            <div className="css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-red-600 text-[16px] text-center w-full">
              <p className="block leading-[24px]">{error}</p>
            </div>
            <button
              onClick={() => fetchUsers()}
              className="hover:underline css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center w-full"
              style={{ color: colors.primary }}
            >
              Попробовать снова
            </button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-[#ffffff] box-border content-stretch flex flex-col gap-4 items-center justify-center p-[32px] relative rounded-[32px] shrink-0 w-full">
            <div className="css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center w-full">
              <p className="block leading-[24px]">Пользователи не найдены</p>
            </div>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-[#ffffff] relative rounded-2xl shrink-0 w-full"
            >
              <div className="flex flex-row items-center relative size-full">
                <div className="box-border content-stretch flex flex-row items-center justify-between p-4 relative w-full">
                  {/* User Info */}
                  <div className="basis-0 box-border content-stretch flex flex-row gap-3 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                    <UserAvatar
                      firstName={user.firstName}
                      lastName={user.lastName}
                      email={user.email}
                      size="md"
                      className="w-10 h-10"
                    />
                    <div className="box-border content-stretch flex flex-col items-start justify-start leading-[0] not-italic p-0 relative shrink-0 text-[14px] tracking-[-0.084px]">
                      <div className="css-5cyu6a font-['Inter:Semi_Bold',_sans-serif] font-semibold relative shrink-0 text-[#000000] text-left w-full">
                        <p className="block leading-[20px]">{user.firstName} {user.lastName}</p>
                      </div>
                      <div className="css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-[#525866] text-left w-full">
                        <p className="block leading-[20px]">{user.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Role and Actions */}
                  <div className="basis-0 box-border content-stretch flex flex-row gap-6 grow items-center justify-end min-h-px min-w-px p-0 relative shrink-0">
                    <div className="css-bbeeyw font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px] mr-6">
                      <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                        {getRoleLabel(user.role)}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="box-border content-stretch flex flex-row gap-3 items-center justify-start p-0 relative shrink-0">
                      {!invitationMode && (
                        <>
                          {canEditUser(user.id) && (
                            <button
                              onClick={() => openUserEditMode(user)}
                              className="hover:opacity-75 transition-opacity"
                              aria-label={`Редактировать ${user.firstName} ${user.lastName}`}
                              title="Редактировать"
                            >
                              <EditIcon />
                            </button>
                          )}
                          {(() => {
                            const currentUserId = useAuthStore.getState().user?.id;
                            if (currentUserId === user.id) {
                              return false;
                            }
                            const canDelete = canDeleteUser();
                            if (typeof canDelete === 'function') {
                              return canDelete(user.role);
                            }
                            return Boolean(canDelete);
                          })() && (
                            <button
                              onClick={() => openDeleteModal(user)}
                              className="hover:opacity-75 transition-opacity"
                              aria-label={`Удалить ${user.firstName} ${user.lastName}`}
                              title="Удалить"
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </>
                      )}
                      {invitationMode && (
                        isUserInTeam(user) ? (
                          <div className="px-4 py-3 rounded-2xl bg-[#f8f9fa] text-[#868c98] css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-center border border-[#e2e4e9]">
                            Уже в команде
                          </div>
                        ) : (
                          <button
                            onClick={() => inviteUser(user)}
                            className="text-white px-4 py-3 rounded-2xl transition-colors css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-center"
                            style={getButtonInlineStyles(colors, 'primary')}
                            onMouseEnter={(e) => {
                              Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary'));
                            }}
                            onMouseLeave={(e) => {
                              Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary'));
                            }}
                          >
                            Пригласить
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Пагинация */}
    </>
  );
  };

  // Рендер формы создания/редактирования
  const renderForm = () => (
    <>
      {/* Header with Back Button */}
      <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
          <p className="block leading-[30px] whitespace-pre">
            {invitationMode ? 'Приглашение в команду' : getHeaderTitle()}
          </p>
        </div>
        
        <button
          onClick={backToList}
          className="transition-colors text-[15px] font-medium"
          style={{ color: colors.primary }}
          onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
          onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
        >
          ← Назад к списку
        </button>
      </div>

      {/* Form */}
      <div className="bg-[#ffffff] box-border content-stretch flex flex-col gap-6 items-start justify-start p-[32px] relative rounded-[32px] shrink-0 w-full">
        <form onSubmit={handleSubmit} className="space-y-6 w-full">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full mb-2">
                <p className="block leading-[20px]">Имя *</p>
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] px-[24px] border border-[#e2e4e9] focus:outline-none h-[44px]"
              />
            </div>
            
            <div>
              <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full mb-2">
                <p className="block leading-[20px]">Фамилия *</p>
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] px-[24px] border border-[#e2e4e9] focus:outline-none h-[44px]"
              />
            </div>
            
            <div>
              <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full mb-2">
                <p className="block leading-[20px]">Email *</p>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] px-[24px] border border-[#e2e4e9] focus:outline-none h-[44px]"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <SelectWithLabel
                label="Роль *"
                value={formData.role}
                onChange={(value) => setFormData(prev => ({ ...prev, role: value as RoleEnum }))}
                options={(() => {
                  const currentRole = String(getCurrentRole() || '').toUpperCase();
                  const hasCompany = formData.companyId != null;
                  const canSetOwner = hasCompany && !companyHasOwner;
                  if (currentRole === 'SUPER_ADMIN') {
                    const options = [
                      { value: RoleEnum.ADMIN, label: 'Администратор' },
                      { value: RoleEnum.RECRUITER, label: 'Рекрутер' }
                    ];
                    if (canSetOwner) {
                      options.unshift({ value: RoleEnum.OWNER, label: 'Владелец' });
                    }
                    return options;
                  } else if (currentRole === 'OWNER') {
                    return [
                      { value: RoleEnum.ADMIN, label: 'Администратор' },
                      { value: RoleEnum.RECRUITER, label: 'Рекрутер' }
                    ];
                  } else if (currentRole === 'ADMIN') {
                    return [
                      { value: RoleEnum.ADMIN, label: 'Администратор' },
                      { value: RoleEnum.RECRUITER, label: 'Рекрутер' }
                    ];
                  }
                  return [{ value: RoleEnum.RECRUITER, label: 'Рекрутер' }];
                })()}
                required={true}
              />
              {isCurrentUserSuperAdmin() && formData.companyId == null && (
                <div className="text-[#868c98] text-[12px] font-['Inter:Regular',_sans-serif]">
                  Чтобы назначить роль "Владелец", необходимо выбрать компанию
                </div>
              )}
              {isCurrentUserSuperAdmin() && formData.companyId != null && companyHasOwner && (
                <div className="text-[#868c98] text-[12px] font-['Inter:Regular',_sans-serif]">
                  В этой компании уже есть владелец
                </div>
              )}
              {checkingOwner && (
                <div className="text-[#868c98] text-[12px] font-['Inter:Regular',_sans-serif]">
                  Проверка...
                </div>
              )}
            </div>
            
            {isCurrentUserSuperAdmin() ? (
              <CompanySelectWithLabel
                label="Компания"
                value={formData.companyId != null ? formData.companyId : 'null'}
                onChange={(value) => {
                  const newCompanyId = value && value !== 'null' ? Number(value) : undefined;
                  setFormData(prev => {
                    const newData = { ...prev, companyId: newCompanyId };
                    if (newCompanyId == null && prev.role === RoleEnum.OWNER) {
                      newData.role = RoleEnum.RECRUITER;
                    }
                    return newData;
                  });
                }}
                companies={companiesForSelect}
                includeNone={true}
                openUpward={true}
                disabled={false}
              />
            ) : (
              <CompanySelectWithLabel
                label="Компания"
                value={myCompany?.id || 'null'}
                onChange={() => { /* выбор компании отключен для администратора */ }}
                companies={companiesForSelect}
                includeNone={false}
                openUpward={true}
                disabled={true}
              />
            )}
            
            {viewMode === 'create' && (
              <>
                <div className="sm:col-span-2">
                  <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full mb-2">
                    <p className="block leading-[20px]">Пароль *</p>
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        password: newPassword,
                        // Если очищается поле пароля, очищаем и поле подтверждения пароля
                        confirmPassword: newPassword ? prev.confirmPassword : ''
                      }));
                    }}
                    autoComplete="new-password"
                    className="bg-[#ffffff] relative rounded-xl shrink-0 w-[555px] max-w-full py-[10px] px-[24px] border border-[#e2e4e9] focus:outline-none h-[44px]"
                  />
                </div>
                
                {/* Поле подтверждения пароля показывается только если введен пароль */}
                {formData.password && (
                  <div className="sm:col-span-2">
                    <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full mb-2">
                      <p className="block leading-[20px]">Подтверждение пароля *</p>
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      autoComplete="new-password"
                      className="bg-[#ffffff] relative rounded-xl shrink-0 w-[555px] max-w-full py-[10px] px-[24px] border border-[#e2e4e9] focus:outline-none h-[44px]"
                      onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e4e9'; }}
                      placeholder="Повторите пароль"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Отображение ошибок внизу формы */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 w-full">
              <div className="text-red-600 text-[14px] font-['Inter:Medium',_sans-serif] font-medium">
                {error}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 text-white p-[10px] h-[44px] rounded-2xl transition-colors css-rpndqk font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px]"
              style={getButtonInlineStyles(colors, 'primary')}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary'));
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary'));
              }}
            >
              {getActionButtonText()}
            </button>
            <button
              type="button"
              onClick={backToList}
              className="flex-1 border border-[#e2e4e9] text-[#525866] p-[10px] h-[44px] rounded-2xl hover:bg-[#f8f9fa] transition-colors css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px]"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </>
  );

  return (
    <div className="bg-[#f5f6f1] box-border content-stretch flex flex-col items-start justify-between p-[24px] relative rounded-[24px] shrink-0 w-full h-full overflow-y-auto">
      <div className="flex flex-col gap-4 w-full">
        {/* В режиме приглашения показываем только список, в режиме управления - список, форма или UserEditForm */}
        {invitationMode ? renderUsersList() : (
          viewMode === 'list' ? renderUsersList() : 
          viewMode === 'user-edit' && editingUserId ? (
            <UserEditForm 
              userId={editingUserId} 
              onBack={backToList}
              onSuccess={handleUserEditSuccess}
              mode="admin"
            />
          ) : renderForm()
        )}
      </div>

      {/* Пагинация внизу общего контейнера - только для режима списка */}
      {viewMode === 'list' && pagination.totalPages > 1 && (
        <ManagementPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={pagination.goToPage}
        />
      )}

      {/* Модалка подтверждения удаления */}
      {deleteModalOpen && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4" role="dialog" aria-modal="true">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2m3 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6h14z"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Удаление аккаунта
              </h3>
              
              <p className="text-gray-600 mb-2 text-center leading-relaxed">
                Вы уверены, что хотите удалить аккаунт
              </p>
              <p className="text-gray-900 font-semibold mb-4 text-center">
                {userToDelete.firstName} {userToDelete.lastName}?
              </p>
              <p className="text-gray-600 text-sm mb-2 text-center">
                Рекомендуем сначала перевести пользователя в неактивные. Это безопаснее и не удаляет данные.
              </p>
              <p className="text-red-500 text-sm mb-6 text-center">
                Удаление нельзя отменить.
              </p>
              
              {/* Отображение ошибки удаления */}
              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm text-center">
                    {deleteError}
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="flex-1 px-[24px] py-[14px] h-[52px] rounded-2xl border border-[#e2e4e9] text-[#525866] hover:bg-[#f8f9fa] transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                >
                  Отмена
                </button>
                {userToDelete.isActive && (
                  <button
                    onClick={confirmDeactivateUser}
                    className="flex-1 px-[24px] py-[14px] h-[52px] rounded-2xl border border-[#e2e4e9] text-[#111827] hover:bg-[#f8f9fa] transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center whitespace-nowrap"
                  >
                    В неактивные
                  </button>
                )}
                <button
                  onClick={confirmDeleteUser}
                  className="flex-1 px-[24px] py-[14px] h-[52px] rounded-2xl bg-red-500 text-white hover:bg-red-600 transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
